import { zodResolver } from '@hookform/resolvers/zod';
import {
  ASSIGNABLE_ROLES,
  type AssignableRole,
  type CreateInvitationResponse,
  emailSchema,
  type MemberDetail,
  PERMISSION_KEYS,
  type PermissionKey,
  ROLE_DEFAULT_PERMISSIONS,
  updateMemberRequestSchema,
  type UpdateMemberRequest,
} from '@familywallet/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { z } from 'zod';
import { FormError } from '../components/FormError';
import { InviteLink } from '../components/InviteLink';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardTitle } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input, Select } from '../components/ui/input';
import { api } from '../lib/api';
import { meQueryKey, useCan, useMe } from '../lib/auth';
import { useLimits } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { formatAmount, groupAmountInput } from '../lib/money';
import { limitFormSchema } from '../lib/planning-forms';
import { OverBadge, RemainingText, StatusBar } from '../components/PlanningCards';
import {
  invitationsKey,
  memberKey,
  membersKey,
  useIdempotencyKey,
  useMember,
} from '../lib/members';

function useRefreshMember(id: string) {
  const queryClient = useQueryClient();
  return async (updated: MemberDetail) => {
    queryClient.setQueryData(memberKey(id), updated);
    await queryClient.invalidateQueries({ queryKey: membersKey, exact: true });
    await queryClient.invalidateQueries({ queryKey: meQueryKey });
  };
}

function DetailsForm({
  member,
  canEdit,
  canChangeRole,
}: {
  member: MemberDetail;
  canEdit: boolean;
  canChangeRole: boolean;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const refresh = useRefreshMember(member.id);
  const form = useForm<UpdateMemberRequest>({
    resolver: zodResolver(updateMemberRequestSchema),
    values: {
      displayName: member.displayName,
      ...(member.role !== 'owner' ? { role: member.role } : {}),
    },
  });
  const role = useWatch({ control: form.control, name: 'role' });
  const save = useMutation({
    mutationFn: (v: UpdateMemberRequest) =>
      api.members.update(member.id, canChangeRole ? v : { displayName: v.displayName }),
    onSuccess: refresh,
  });
  const roles = ASSIGNABLE_ROLES.filter(
    (r) => r !== 'parent' || me.data?.member.role === 'owner' || member.role === 'parent',
  );

  return (
    <Card>
      <CardTitle>{t('memberDetail.edit')}</CardTitle>
      <form
        noValidate
        className="mt-3 flex flex-col gap-4"
        onSubmit={form.handleSubmit((v) => save.mutate(v))}
      >
        <FormError error={save.error} />
        <Field label={t('fields.memberName')} error={form.formState.errors.displayName}>
          <Input disabled={!canEdit} {...form.register('displayName')} />
        </Field>
        {member.role === 'owner' ? (
          <p className="text-sm text-muted">{t('roles.owner')}</p>
        ) : (
          <Field
            label={t('fields.role')}
            hint={t(`roleHints.${(role ?? member.role) as AssignableRole}`)}
          >
            <Select disabled={!canChangeRole} {...form.register('role')}>
              {roles.map((role) => (
                <option key={role} value={role}>
                  {t(`roles.${role}`)}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {canEdit && (
          <Button type="submit" disabled={save.isPending}>
            {save.isSuccess && !form.formState.isDirty ? t('common.saved') : t('common.save')}
          </Button>
        )}
      </form>
    </Card>
  );
}

function StatusCard({ member }: { member: MemberDetail }) {
  const { t } = useTranslation();
  const refresh = useRefreshMember(member.id);
  const change = useMutation({
    mutationFn: () =>
      member.status === 'active'
        ? api.members.deactivate(member.id)
        : api.members.reactivate(member.id),
    onSuccess: refresh,
  });
  function onClick() {
    if (
      member.status === 'active' &&
      !window.confirm(t('memberDetail.deactivateConfirm', { name: member.displayName }))
    )
      return;
    change.mutate();
  }
  return (
    <Card className="flex flex-col gap-3">
      <CardTitle>{t('memberDetail.status')}</CardTitle>
      <FormError error={change.error} />
      <p>{member.status === 'active' ? t('memberDetail.active') : t('memberDetail.deactivated')}</p>
      <Button
        variant={member.status === 'active' ? 'danger' : 'secondary'}
        onClick={onClick}
        disabled={change.isPending}
      >
        {member.status === 'active' ? t('memberDetail.deactivate') : t('memberDetail.reactivate')}
      </Button>
    </Card>
  );
}

const inviteSchema = z.object({ email: emailSchema });

function InviteCard({ member }: { member: MemberDetail }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [idempotencyKey, renewKey] = useIdempotencyKey();
  const [created, setCreated] = useState<CreateInvitationResponse | null>(null);
  const form = useForm<z.input<typeof inviteSchema>, unknown, z.output<typeof inviteSchema>>({
    resolver: zodResolver(inviteSchema),
  });
  const invite = useMutation({
    mutationFn: (v: z.output<typeof inviteSchema>) =>
      api.invitations.create({ email: v.email, memberId: member.id }, { idempotencyKey }),
    onSuccess: async (result) => {
      renewKey();
      setCreated(result);
      await queryClient.invalidateQueries({ queryKey: invitationsKey });
    },
  });
  if (created) return <InviteLink url={created.inviteUrl} name={member.displayName} />;
  return (
    <Card>
      <CardTitle>{t('memberDetail.invite')}</CardTitle>
      <p className="mt-1 text-sm text-muted">{t('invite.intro', { name: member.displayName })}</p>
      <form
        noValidate
        className="mt-3 flex flex-col gap-4"
        onSubmit={form.handleSubmit((v) => invite.mutate(v))}
      >
        <FormError error={invite.error} />
        <Field label={t('invite.email')} error={form.formState.errors.email} kind="email">
          <Input type="email" inputMode="email" autoComplete="off" {...form.register('email')} />
        </Field>
        <Button type="submit" disabled={invite.isPending}>
          {t('invite.submit')}
        </Button>
      </form>
    </Card>
  );
}

type Choice = 'default' | 'allow' | 'deny';

function PermissionsCard({ member }: { member: MemberDetail }) {
  const { t } = useTranslation();
  const refresh = useRefreshMember(member.id);
  const initial = Object.fromEntries(
    PERMISSION_KEYS.map((key) => {
      const o = member.overrides.find((x) => x.key === key);
      return [key, o ? (o.allowed ? 'allow' : 'deny') : 'default'];
    }),
  ) as Record<PermissionKey, Choice>;
  const [choices, setChoices] = useState(initial);
  const save = useMutation({
    mutationFn: () =>
      api.members.setPermissions(member.id, {
        overrides: PERMISSION_KEYS.filter((k) => choices[k] !== 'default').map((key) => ({
          key,
          allowed: choices[key] === 'allow',
        })),
      }),
    onSuccess: refresh,
  });
  const defaults = ROLE_DEFAULT_PERMISSIONS[member.role];
  // Owner-only: never offered as "allow". Private accounts are never possible for a child.
  const keys = PERMISSION_KEYS.filter(
    (k) => k !== 'permissions.manage' && !(k === 'accounts.private' && member.role === 'child'),
  );

  return (
    <Card>
      <CardTitle>{t('memberDetail.permissionsTitle')}</CardTitle>
      <p className="mt-1 text-sm text-muted">{t('memberDetail.permissionsIntro')}</p>
      <div className="mt-3 flex flex-col gap-3">
        <FormError error={save.error} />
        {keys.map((key) => (
          <Field key={key} label={t(`permissions.${key}`)}>
            <Select
              value={choices[key]}
              onChange={(e) => setChoices({ ...choices, [key]: e.target.value as Choice })}
            >
              <option value="default">
                {t('memberDetail.permissionDefault', {
                  value: defaults.includes(key) ? t('memberDetail.yes') : t('memberDetail.no'),
                })}
              </option>
              <option value="allow">{t('memberDetail.allow')}</option>
              <option value="deny">{t('memberDetail.deny')}</option>
            </Select>
          </Field>
        ))}
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {t('memberDetail.savePermissions')}
        </Button>
      </div>
    </Card>
  );
}

export function MemberDetailPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const me = useMe();
  const member = useMember(id);
  const canManage = useCan('members.manage');
  const canInvite = useCan('members.invite');
  const canSetPermissions = useCan('permissions.manage');

  if (member.isPending || !me.data) return <p role="status">{t('app.loading')}</p>;
  if (member.isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('members.title')} back="/members" />
        <FormError error={member.error} />
      </div>
    );
  }

  const m = member.data;
  const myRole = me.data.member.role;
  const isMe = m.id === me.data.member.id;
  // Mirrors the server's policy only to hide buttons; the API decides.
  const ownerOrSelf =
    m.role === 'owner' ? isMe : m.role === 'parent' ? myRole === 'owner' || isMe : true;
  const canEdit = (m.role === 'owner' && isMe) || (canManage && ownerOrSelf);
  const canChangeRole = canEdit && m.role !== 'owner' && !isMe;
  const canChangeStatus =
    canManage && m.role !== 'owner' && !isMe && (m.role !== 'parent' || myRole === 'owner');
  const showInvite =
    canInvite &&
    !m.hasLogin &&
    m.status === 'active' &&
    m.role !== 'owner' &&
    (m.role !== 'parent' || myRole === 'owner');
  const showPermissions = canSetPermissions && m.role !== 'owner' && !isMe;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={m.displayName} back="/members" />
      <DetailsForm member={m} canEdit={canEdit} canChangeRole={canChangeRole} />
      {showInvite && <InviteCard member={m} />}
      {canChangeStatus && <StatusCard member={m} />}
      {m.role === 'child' && m.status === 'active' && (
        <LimitCard memberId={m.id} name={m.displayName} />
      )}
      {showPermissions && (
        <PermissionsCard key={m.overrides.map((o) => `${o.key}:${o.allowed}`).join()} member={m} />
      )}
    </div>
  );
}

/** A child's monthly limit: this month's spending, and (for parents) setting the amount. */
function LimitCard({ memberId, name }: { memberId: string; name: string }) {
  const { t, i18n } = useTranslation();
  const limits = useLimits();
  const canManage = useCan('limits.manage');
  const invalidate = useInvalidateLedger();
  const item = limits.data?.items.find((l) => l.memberId === memberId);
  const currency = limits.data?.currency ?? 'USD';
  const resolver = useMemo(() => zodResolver(limitFormSchema(currency)), [currency]);
  const form = useForm<{ amount: string }, unknown, { amount: string }>({
    resolver,
    values: { amount: item?.amount ? groupAmountInput(item.amount, currency) : '' },
  });
  const save = useMutation({
    mutationFn: (amount: string) => api.limits.set(memberId, amount),
    onSuccess: invalidate,
  });
  if (!limits.data || !item) return null;
  const amountError = form.formState.errors.amount?.message;
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <CardTitle className="flex-1">{t('limits.title')}</CardTitle>
        {item.over && <OverBadge label={t('limits.over')} />}
      </div>
      {item.amount !== null && item.left !== null ? (
        <>
          <div className="flex items-baseline justify-between gap-3">
            <span dir="ltr" className="text-[13px] text-muted tabular-nums">
              {t('budgets.spentOf', {
                spent: formatAmount(item.spent, currency, i18n.language),
                amount: formatAmount(item.amount, currency, i18n.language),
              })}
            </span>
            <RemainingText remaining={item.left} currency={currency} overKey="limits.overBy" />
          </div>
          <StatusBar
            percent={item.percent}
            status={item.status ?? 'normal'}
            label={t('limits.title')}
          />
        </>
      ) : (
        <span className="text-[13px] text-muted">
          {t('limits.none')} ·{' '}
          {t('limits.spent', { amount: formatAmount(item.spent, currency, i18n.language) })}
        </span>
      )}
      <p className="text-xs text-muted">{t('limits.hint', { name, currency })}</p>
      {canManage && (
        <form
          noValidate
          className="flex flex-col gap-3"
          onSubmit={form.handleSubmit((body) => save.mutate(body.amount))}
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="limit-amount" className="text-[13px] font-medium text-muted">
              {t('limits.amount', { currency })}
            </label>
            <Input
              id="limit-amount"
              inputMode="decimal"
              autoComplete="off"
              dir="ltr"
              placeholder="0.00"
              aria-invalid={amountError ? true : undefined}
              {...form.register('amount')}
            />
            {amountError && <p className="text-sm text-danger">{t(amountError)}</p>}
          </div>
          <FormError error={save.error} />
          {save.isSuccess && !form.formState.isDirty && (
            <p role="status" className="text-sm text-muted">
              {t('limits.saved')}
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button type="submit" variant="secondary" disabled={save.isPending}>
              {t('limits.save')}
            </Button>
            {item.amount !== null && (
              <Button variant="danger" disabled={save.isPending} onClick={() => save.mutate('0')}>
                {t('limits.remove')}
              </Button>
            )}
          </div>
        </form>
      )}
    </Card>
  );
}
